import { store, Unsubscribe } from "@stencil/redux";
import {
  IMember,
  ConversationTypeEnum,
  CobrowseRequestType,
} from "@auvious/integrations";
import {
  FilterType,
  IFilter,
  CobrowserError,
  CobrowserErrors,
} from "@auvious/cobrowser";
import {
  Component,
  State,
  ComponentInterface,
  h,
  Host,
  Prop,
  EventEmitter,
  Event,
} from "@stencil/core";

import {
  CobrowseService,
  TranslateService,
  AppConfigService as Config,
  ThemeService,
  DeviceService,
  CommonService,
  ErrorHandler,
} from "../../services";
import { NotificationService } from "../../services/notification.service";
import { RootState } from "../../store/states";
import {
  CobrowseOriginEnum,
  CobrowsePermissionEnum,
  CobrowseStateEnum,
  DisplayCaptureDirectionEnum,
  DisplayCaptureStateEnum,
  ErrorCode,
  ICobrowsePermissions,
  ICobrowseSenderEventHandlers,
  IPointerPosition,
  IThemeOptions,
} from "../../models";
import {
  dispatchCobrowseStateChange,
  dispatchCobrowseOriginChange,
  dispatchCobrowseRemoteEndpointMetadataChange,
  dispatchCobrowseTicketChange,
  dispatchCobrowsePermissionChange,
} from "../../store/actions/cobrowse";
import { HandledError } from "../../models/HandledError";
import { AVBusService } from "../../services/bus.service";
import { IEndpointMetadata } from "../../models/IEndpointMetadata";
import { AppConfigService } from "../../services/app.config.service";
import {
  dispatchDisplayCaptureStateChange,
  dispatchDisplayCaptureError,
  dispatchDisplayCaptureDirectionChange,
} from "../../store/actions/displayCapture";
import { dispatchConversationMinimizeChange } from "../../store/actions";
import { IPublishedStream, IStream } from "@auvious/rtc";

interface ICobrowseState {
  ticket?: string;
  isOpenTicketDialog?: boolean;
  isViewPermissionEnabled?: boolean;
  loading?: boolean;
  error?: string;
  pointerPosition?: IPointerPosition;
  isTicketInvalid?: boolean;
  screenStream?: IStream;
  streamWidth?: number;
  streamHeight?: number;
  isStreamLoading?: boolean;
  isStreamMetadataLoaded?: boolean;
  isDisclaimerAccepted?: boolean;
}

const DEFAULT_COBROWSE_STATE: ICobrowseState = {
  ticket: null,
  isOpenTicketDialog: false,
  isViewPermissionEnabled: false,
  loading: false,
  error: null,
  pointerPosition: { top: 0, left: 0 },
  isTicketInvalid: false,
  screenStream: undefined,
  isStreamLoading: false,
  isStreamMetadataLoaded: false,
  streamWidth: 1,
  streamHeight: 1,
  isDisclaimerAccepted: false,
};

const STYLESHEET_ID = "app-auvious-cobrowse-style";
const BORDER_COLOR = "#3366ff";

@Component({
  tag: "app-cobrowse",
  styleUrl: "app-cobrowse.scss",
  shadow: true,
})
export class AppCobrowse implements ComponentInterface {
  @Event({ bubbles: false }) starting: EventEmitter;
  @Event({ bubbles: false }) terminated: EventEmitter;
  @Event({ bubbles: false }) started: EventEmitter<string>;
  @Event({ bubbles: false }) connectFailed: EventEmitter;
  @Event({ bubbles: false }) containerReady: EventEmitter<HTMLDivElement>;

  // @State() open: boolean = false;
  // @Watch("open")
  // openChange(newValue: boolean) {
  //   if (newValue) {
  //     if (!CobrowseService.isAuthenticated()) {
  //       this.setViewState({ isOpenTicketDialog: true });
  //       this.dispatchCobrowseStateChange(CobrowseStateEnum.ticketRequest);
  //     }
  //   } else {
  //     this.setViewState({ isOpenTicketDialog: false, ticket: null });
  //   }
  // }

  // @Watch("$ticket")
  ticketChange(newValue: string) {
    this.setViewState({ ticket: newValue });
    // for some reason if we have screen share on and terminate the session, the component gets destroyed and recreated
    // this causes the ticket change to fire and an authentication flow while terminating...
    // TODO: Why is the component destroyed if we close while we screen share?
    if (!newValue || this.$cobrowseState === CobrowseStateEnum.terminating) {
      return;
    }
    this.ticketReady();
  }

  @State() viewState: ICobrowseState = { ...DEFAULT_COBROWSE_STATE };

  @Prop({ mutable: true }) $cobrowseState: CobrowseStateEnum;
  @Prop({ mutable: true }) $cobrowseOrigin: CobrowseOriginEnum;
  @Prop({ mutable: true }) $customer: IMember;
  @Prop({ mutable: true }) $cobrowseRemoteEndpointMetadata: IEndpointMetadata;
  @Prop({ mutable: true }) $ticket: string;
  @Prop({ mutable: true }) $conversationType: ConversationTypeEnum;
  @Prop({ mutable: true }) $cobrowseRequestType: CobrowseRequestType;
  @Prop({ mutable: true }) $permissions: {
    [key in CobrowsePermissionEnum]: boolean;
  };
  @Prop({ mutable: true }) $displayCaptureState: DisplayCaptureStateEnum;
  @Prop({ mutable: true })
  $displayCaptureDirection: DisplayCaptureDirectionEnum;

  dispatchCobrowseStateChange: typeof dispatchCobrowseStateChange;
  dispatchCobrowseOriginChange: typeof dispatchCobrowseOriginChange;
  dispatchDisplayCaptureStateChange: typeof dispatchDisplayCaptureStateChange;
  dispatchDisplayCaptureError: typeof dispatchDisplayCaptureError;
  dispatchCobrowseTicketChange: typeof dispatchCobrowseTicketChange;
  dispatchCobrowsePermissionChange: typeof dispatchCobrowsePermissionChange;
  dispatchCobrowseRemoteEndpointMetadataChange: typeof dispatchCobrowseRemoteEndpointMetadataChange;
  dispatchConversationMinimizeChange: typeof dispatchConversationMinimizeChange;
  dispatchDisplayCaptureDirectionChange: typeof dispatchDisplayCaptureDirectionChange;

  cobrowseHandlers: ICobrowseSenderEventHandlers;

  agentGotSickNotificationId: string;
  disconnectedTimeout: any;
  storeUnsubscribe: Unsubscribe;
  themeOptions: IThemeOptions;
  conferenceId: string;
  interactionId: string;
  ticketLength: number;
  isMobile = false;
  videoRef: HTMLVideoElement;
  confirmNotification = {
    displayCapture: undefined,
    view: undefined,
    control: undefined,
  };

  constructor() {
    this.cobrowseHandlers = {
      terminated: this.onTerminated.bind(this),
      controlRequested: this.onControlRequested.bind(this),
      mouseMove: this.onMouseMove.bind(this),
      viewRequested: this.onViewRequested.bind(this),
      agentGotSick: this.onAgentGotSick.bind(this),
      agentRecovered: this.onAgentRecovered.bind(this),
      agentLeft: this.onAgentLeft.bind(this),
      resumed: this.onResumed.bind(this),
      displayCaptureRequested: this.onDisplayCaptureRequested.bind(this),
      displayCaptureRejected: this.onDisplayCaptureRejected.bind(this),
      displayCaptureLocalStreamPublished:
        this.onLocalStreamPublished.bind(this),
      displayCaptureLocalStreamRemoved: this.onLocalStreamRemoved.bind(this),
      displayCaptureRemoteStreamAdded: this.onRemoteStreamAdded.bind(this),
      displayCaptureRemoteStreamRemoved: this.onRemoteStreamRemoved.bind(this),
    };
    ThemeService.optionsAvailable().subscribe((o) => (this.themeOptions = o));
    this.isMobile = DeviceService.isMobile;
  }

  connectedCallback() {
    if (this.storeUnsubscribe) this.storeUnsubscribe();

    this.storeUnsubscribe = store.mapStateToProps(
      this,
      (rootState: RootState) => {
        const {
          cobrowse: {
            state,
            origin,
            remoteEndpointMetadata,
            ticket,
            permissions,
            requestType,
          },
          conversation: { customer, type },
          displayCapture: { direction },
        } = rootState;
        const dcState = rootState.displayCapture.state;
        return {
          $cobrowseState: state,
          $cobrowseOrigin: origin,
          $customer: customer,
          $cobrowseRemoteEndpointMetadata: remoteEndpointMetadata,
          $ticket: ticket,
          $permissions: permissions,
          $conversationType: type,
          $cobrowseRequestType: requestType,
          $displayCaptureState: dcState,
          $displayCaptureDirection: direction,
        };
      }
    );
    store.mapDispatchToProps(this, {
      dispatchCobrowseStateChange,
      dispatchCobrowseOriginChange,
      dispatchDisplayCaptureStateChange: dispatchDisplayCaptureStateChange,
      dispatchDisplayCaptureError: dispatchDisplayCaptureError,
      dispatchCobrowseTicketChange,
      dispatchCobrowsePermissionChange,
      dispatchCobrowseRemoteEndpointMetadataChange,
      dispatchConversationMinimizeChange,
      dispatchDisplayCaptureDirectionChange,
    });
    this.ticketLength =
      AppConfigService.ticketLength +
      (AppConfigService.ticketGroupsEnabled
        ? AppConfigService.ticketLength / 3 - 1
        : 0);
  }

  componentDidLoad(): void {
    if (this.$ticket) {
      this.ticketChange(this.$ticket);
    } else {
      if (!CobrowseService.isAuthenticated()) {
        this.setViewState({ isOpenTicketDialog: true });
        this.dispatchCobrowseStateChange(CobrowseStateEnum.ticketRequest);
      }
    }
  }

  disconnectedCallback() {
    this.setViewState({ isOpenTicketDialog: false, ticket: null });
    this.storeUnsubscribe();
  }

  setTicketID(ticket) {
    this.setViewState({ ticket, isTicketInvalid: false });
  }

  private setViewState(props: ICobrowseState) {
    this.viewState = { ...this.viewState, ...props };
  }

  private addCSSRule(rules: string) {
    const style = document.getElementById(STYLESHEET_ID);
    // @ts-ignore
    !!style?.styleSheet
      ? // @ts-ignore
        (style.styleSheet.cssText = rules)
      : style.appendChild(document.createTextNode(rules));
  }

  private insertHalo() {
    // create style placeholder. This will get updated on view accept.
    const style = document.createElement("style");
    style.id = STYLESHEET_ID;
    document.head.appendChild(style);

    const rules = `body:before{
      content: '';
      position: fixed;
      top: 0;left:0;right: 0;bottom: 0;
      transition: all 0.3s ease-out;
      z-index:999999;
      pointer-events: none;
   }`;
    this.addCSSRule(rules);
  }

  private async connect(interactionId: string) {
    try {
      ErrorHandler.addBreadcrumb("Co-browse | connect");
      this.setViewState({ loading: true });
      let filters: IFilter[] = [];

      if (this.isCobrowseRequest) {
        this.insertHalo();

        filters = Config.cobrowseFilters?.map((selector) => {
          return { selector, type: Config.cobrowseFilterType };
        });

        filters.push({
          selector: `#${STYLESHEET_ID}`,
          type: FilterType.NoDisplay,
        });
      }

      const requestType = this.$cobrowseRequestType || "view";

      // connect to RTC + cobrowse
      await CobrowseService.connect(
        this.cobrowseHandlers,
        filters,
        this.$customer?.displayName || AppConfigService.customerDisplayName,
        requestType
      );

      this.setViewState({ isOpenTicketDialog: false });
      this.dispatchCobrowseStateChange(CobrowseStateEnum.connected);

      // we need this for page refresh
      CobrowseService.setRequestType(requestType);

      this.started.emit(interactionId);
    } catch (ex) {
      ErrorHandler.addBreadcrumb("Co-browse | connect failed", { ex });
      if (ex.data?.status === 404) {
        NotificationService.error("PIN already used", {
          body: "Please request for a new one.",
          errorCode: ErrorCode.COBROWSE_INVALID_PIN,
        });
      } else {
        NotificationService.error("Error", {
          body: ex.message,
          errorCode: ErrorCode.COBROWSE_API_ERROR,
        });
      }

      if (this.$cobrowseOrigin === CobrowseOriginEnum.widget) {
        // somehow the PIN is an old one, show the dialog again and remove the previous ticket
        this.setViewState({ isOpenTicketDialog: true, ticket: null });

        this.dispatchCobrowseStateChange(CobrowseStateEnum.ticketRequest);

        this.connectFailed.emit();
      } else {
        // we are in a chat or call, remove all co-browse related stuff and wait for a new request
        this.notifyTerminated(this.viewState.ticket);

        this.dispatchCobrowseStateChange(CobrowseStateEnum.idle);

        this.resetState();
      }

      CobrowseService.clearTicket();
    } finally {
      this.setViewState({ loading: false });
    }
  }

  async denyView() {
    ErrorHandler.addBreadcrumb("Co-browse | view denied");
    this.confirmNotification.view = null;
    CobrowseService.denyViewAccess();
    this.dispatchCobrowsePermissionChange({
      [CobrowsePermissionEnum.view]: false,
    });
    // this.setViewState({ isRequestingView: false });
  }

  async confirmView() {
    ErrorHandler.addBreadcrumb("Co-browse | view approved");
    try {
      this.confirmNotification.view = null;
      CobrowseService.allowViewAccess();
      this.dispatchCobrowsePermissionChange({
        [CobrowsePermissionEnum.view]: true,
      });
      if (this.$conversationType === ConversationTypeEnum.chat) {
        this.dispatchConversationMinimizeChange(true);
      }
      this.viewPermissionEnabled();
    } catch (ex) {
      if (ex instanceof CobrowserError) {
        if (ex.type === CobrowserErrors.Cancelled) {
          NotificationService.error("Error", {
            body: ex.message,
            errorCode: ErrorCode.COBROWSE_API_ERROR,
          });
        }
      }
      this.notifyTerminated(this.viewState.ticket);
      this.resetState();
    }
  }

  notifyTerminated(ticket: string) {
    ErrorHandler.addBreadcrumb("Co-browse | terminated");
    // stored a used ticket in case we refresh
    if (
      [
        ConversationTypeEnum.chat,
        ConversationTypeEnum.videoCall,
        ConversationTypeEnum.voiceCall,
      ].includes(this.$conversationType)
    ) {
      CobrowseService.setUsedTicket(ticket);
    } else {
      CobrowseService.clearUsedTickets();
    }
    if (this.$conversationType === ConversationTypeEnum.chat) {
      this.dispatchConversationMinimizeChange(false);
    }
    this.terminated.emit();
  }

  approveDisplayCapture() {
    this.confirmNotification.displayCapture = undefined;
    ErrorHandler.addBreadcrumb("Co-browse | display-capture approved");
    CobrowseService.startDisplayCapture();
    this.dispatchCobrowsePermissionChange({
      [CobrowsePermissionEnum.displayCapture]: true,
    });
    this.dispatchDisplayCaptureStateChange(
      DisplayCaptureStateEnum.loadingStream
    );
    if (
      this.$conversationType === ConversationTypeEnum.chat &&
      this.isDisplayCaptureRequest
    ) {
      this.dispatchConversationMinimizeChange(true);
    }
  }

  denyDisplayCapture() {
    this.confirmNotification.displayCapture = undefined;
    ErrorHandler.addBreadcrumb("Co-browse | display-capture denied");
    CobrowseService.denyDisplayCapture();
    this.dispatchCobrowsePermissionChange({
      [CobrowsePermissionEnum.displayCapture]: false,
    });
    this.dispatchDisplayCaptureStateChange(null);
    if (
      this.$conversationType === ConversationTypeEnum.chat &&
      this.isDisplayCaptureRequest
    ) {
      this.dispatchConversationMinimizeChange(false);
      CobrowseService.terminate("user-denied-access");
    }
  }

  async denyControl() {
    this.confirmNotification.control = undefined;
    ErrorHandler.addBreadcrumb("Co-browse | control denied");
    CobrowseService.denyControlAccess();
    this.dispatchCobrowsePermissionChange({
      [CobrowsePermissionEnum.control]: false,
    });
  }

  async confirmControl() {
    this.confirmNotification.control = undefined;
    ErrorHandler.addBreadcrumb("Co-browse | control approved");
    CobrowseService.allowControlAccess();
    this.dispatchCobrowsePermissionChange({
      [CobrowsePermissionEnum.control]: true,
    });
  }

  async ticketReady() {
    if (!this.viewState.ticket) {
      return;
    }
    try {
      ErrorHandler.addBreadcrumb("Co-browse | authenticate");
      this.starting.emit();
      this.dispatchCobrowseStateChange(CobrowseStateEnum.connecting);
      this.setViewState({ loading: true });

      CobrowseService.setTicket(this.viewState.ticket);

      // authenticate user
      const { interactionId } = await CobrowseService.authenticate(
        this.viewState.ticket
      );

      // connect to cobrowser
      this.connect(interactionId);
    } catch (ex) {
      ErrorHandler.addBreadcrumb("Co-browse | authenticate failed", { ex });
      if (this.$cobrowseOrigin === CobrowseOriginEnum.widget) {
        if (ex instanceof HandledError) {
          AVBusService.errorLog(ex);
        } else {
          AVBusService.errorLog(
            new HandledError(ex, ErrorCode.COBROWSE_AUTHENTICATE_FAILED)
          );
        }
        // authenticate failed. show ticket dialog again
        this.setViewState({
          isOpenTicketDialog: true,
          loading: false,
          ticket: null,
        });
        this.dispatchCobrowseStateChange(CobrowseStateEnum.ticketRequest);
      } else {
        if (
          ex instanceof HandledError &&
          ex.code === ErrorCode.SECURITY_APPLICATION_ID_MISMATCH
        ) {
          AVBusService.errorLog(ex);
        } else if (ex.message.includes("events server")) {
          // RTC failed to connect
          NotificationService.error("An error occured", {
            body: "Please check your network connection",
            error: ex,
            errorCode: ErrorCode.COBROWSE_CONNECTION_FAILED,
          });
        } else if (["404", "401"].includes(ex.message)) {
          // ticket not found or already used. Handled by authenticate()
          AVBusService.errorLog(
            new HandledError(ex, ErrorCode.COBROWSE_AUTHENTICATE_FAILED)
          );
        } else {
          // something else happened
          NotificationService.error(ex.message, {
            body: "Please request for a new co-browse session.",
            error: ex,
            errorCode: ErrorCode.COBROWSE_CONNECTION_FAILED,
          });
        }

        this.dispatchCobrowseStateChange(CobrowseStateEnum.idle);
      }
      CobrowseService.clearTicket();
    }
  }

  private start() {
    ErrorHandler.addBreadcrumb("Co-browse | start");
    if (
      !this.viewState.ticket ||
      this.viewState.ticket?.length !== this.ticketLength
    ) {
      this.setViewState({ isTicketInvalid: true });
    } else {
      this.dispatchCobrowseOriginChange(CobrowseOriginEnum.widget);
      this.dispatchCobrowseTicketChange(this.viewState.ticket);
      this.ticketChange(this.viewState.ticket);
    }
  }

  controlPermissionChange() {
    if (this.isControlled) {
      CobrowseService.revokeControlAccess();
      this.dispatchCobrowsePermissionChange({
        [CobrowsePermissionEnum.control]: false,
      });
    }
  }

  terminate() {
    ErrorHandler.addBreadcrumb("Co-browse | terminated");
    // cobrowse session terminated by local user action
    CobrowseService.leave();
    this.setViewState({ isOpenTicketDialog: false, ticket: null });
  }

  viewPermissionEnabled() {
    this.setViewState({
      // isRequestingView: false,
      isViewPermissionEnabled: true,
    });
    const rules = `
    body:before{
      box-shadow: inset 0 0 10px 5px ${BORDER_COLOR};
    }`;
    this.addCSSRule(rules);
  }

  /** handlers */

  onVideoElementLoadedMetadata() {
    this.videoRef?.play();
    this.setViewState({
      isStreamLoading: false,
      isStreamMetadataLoaded: true,
      streamWidth: this.videoRef?.videoWidth,
      streamHeight: this.videoRef?.videoHeight,
    });
  }

  onVideoElementRef(e) {
    if (!this.videoRef) {
      this.videoRef = e;
    }
  }

  onVideoElementFullScreen() {
    if (this.videoRef) {
      ThemeService.requestFullscreen(this.videoRef);
    }
  }

  onAgentGotSick() {
    ErrorHandler.addBreadcrumb("Co-browse | agent got sick");
    // wait 5 seconds in case we get an 'agent recovered' event
    this.disconnectedTimeout = CommonService.timer.setTimeout(() => {
      // keep the id so we can dismiss it on recover.
      this.agentGotSickNotificationId = NotificationService.warning(
        "Co-browse",
        { body: "Agent seems to be having some connectivity issues." }
      );
      // show message and clear timeout
      this.disconnectedTimeout = null;
    }, 5000);
  }

  onAgentRecovered() {
    ErrorHandler.addBreadcrumb("Co-browse | agent recovered");
    if (!this.disconnectedTimeout && this.agentGotSickNotificationId) {
      NotificationService.dismiss(this.agentGotSickNotificationId);
      this.agentGotSickNotificationId = null;
      NotificationService.success("Co-browse", {
        body: "Agent re-connected.",
        ttl: 2000,
      });
    } else {
      // we got a quick reconnect (below 5 secs), clear the timeout
      CommonService.timer.clearTimeout(this.disconnectedTimeout);
      this.disconnectedTimeout = null;
    }
  }

  onAgentLeft() {
    ErrorHandler.addBreadcrumb("Co-browse | agent left");
    if (this.agentGotSickNotificationId) {
      NotificationService.warning("Co-browse", {
        body: "Agent left. Closing session...",
      });
      NotificationService.dismiss(this.agentGotSickNotificationId);
      this.agentGotSickNotificationId = null;
      // todo, leave?
      this.terminate();
    }
  }

  onViewRequested() {
    ErrorHandler.addBreadcrumb("Co-browse | view requested");
    if (this.$cobrowseOrigin === CobrowseOriginEnum.widget) {
      this.confirmView();
    } else {
      this.confirmNotification.view = NotificationService.confirm(
        TranslateService.instant(
          "Agent is requesting access to view your page."
        ),
        {
          onApprove: this.confirmView.bind(this),
          onReject: this.denyView.bind(this),
        }
      );
      // this.setViewState({ isRequestingView: true });
    }
  }

  onResumed(permissions: ICobrowsePermissions) {
    ErrorHandler.addBreadcrumb("Co-browse | resumed");
    this.viewPermissionEnabled();
    this.dispatchCobrowsePermissionChange({
      [CobrowsePermissionEnum.view]: permissions.view,
      [CobrowsePermissionEnum.control]: permissions.control,
    });
  }

  onTerminated() {
    ErrorHandler.addBreadcrumb("Co-browse | terminated");

    if (
      CobrowseService.getTerminateReason() !== "user-denied-access" &&
      ![CobrowseStateEnum.idle, CobrowseStateEnum.ticketRequest].includes(
        this.$cobrowseState
      )
    ) {
      let body =
        this.$cobrowseRequestType === "view"
          ? "Your browser page is no longer shared with the agent."
          : "Your screen can no longer be shared with the agent.";
      if (
        !!this.confirmNotification.view ||
        !!this.confirmNotification.displayCapture
      ) {
        body = "The request was abruptly terminated";
      }

      NotificationService.info(
        this.$cobrowseRequestType === "view"
          ? "Co-browse ended"
          : "Session ended",
        { body }
      );
    }

    // dismiss all pending notifications
    Object.keys(this.confirmNotification).forEach((key) => {
      if (!!this.confirmNotification[key]) {
        NotificationService.dismiss(this.confirmNotification[key]);
        this.confirmNotification[key] = undefined;
      }
    });

    this.notifyTerminated(this.viewState.ticket);

    this.resetState();
  }

  onControlRequested() {
    ErrorHandler.addBreadcrumb("Co-browse | control requested");

    this.confirmNotification.control = NotificationService.confirm(
      TranslateService.instant(
        "Agent is requesting access to control your page."
      ),
      {
        onApprove: this.confirmControl.bind(this),
        onReject: this.denyControl.bind(this),
      }
    );
  }

  onMouseMove(ev) {
    this.setViewState({ pointerPosition: { top: ev.top, left: ev.left } });
  }

  onDisplayCaptureRequested(confirm: boolean) {
    ErrorHandler.addBreadcrumb("Co-browse | display-capture requested");
    if (confirm) {
      this.dispatchDisplayCaptureStateChange(
        DisplayCaptureStateEnum.requestingAccess
      );
      this.confirmNotification.displayCapture = NotificationService.confirm(
        TranslateService.instant(
          "Agent is requesting access to view your screen."
        ),
        {
          onApprove: this.approveDisplayCapture.bind(this),
          onReject: this.denyDisplayCapture.bind(this),
        }
      );
    } else {
      this.approveDisplayCapture();
    }
  }

  onDisplayCaptureRejected() {
    ErrorHandler.addBreadcrumb("Co-browse | display-capture rejected");
    this.denyDisplayCapture();
    this.dispatchDisplayCaptureStateChange(null);
  }

  onLocalStreamPublished(stream: IPublishedStream) {
    ErrorHandler.addBreadcrumb("Co-browse | local stream published");
    this.dispatchDisplayCaptureStateChange(DisplayCaptureStateEnum.sharing);
    this.dispatchDisplayCaptureDirectionChange(
      DisplayCaptureDirectionEnum.outgoing
    );
    this.setViewState({ screenStream: stream, isStreamLoading: true });
    this.attachVideo(stream);
  }

  onLocalStreamRemoved(stream: IStream) {
    ErrorHandler.addBreadcrumb("Co-browse | local stream removed");
    this.dispatchDisplayCaptureStateChange(null);
    this.dispatchDisplayCaptureDirectionChange(null);
    this.dispatchCobrowsePermissionChange({
      [CobrowsePermissionEnum.displayCapture]: false,
    });
    if (
      this.$conversationType === ConversationTypeEnum.chat &&
      this.isDisplayCaptureRequest &&
      !this.$displayCaptureState
    ) {
      this.dispatchConversationMinimizeChange(false);
    }
    this.resetStream(stream);
  }

  onRemoteStreamAdded(stream: IStream) {
    ErrorHandler.addBreadcrumb("Co-browse | remote stream added");
    this.dispatchDisplayCaptureStateChange(DisplayCaptureStateEnum.sharing);
    this.dispatchDisplayCaptureDirectionChange(
      DisplayCaptureDirectionEnum.incoming
    );
    if (this.$conversationType === ConversationTypeEnum.chat) {
      this.dispatchConversationMinimizeChange(true);
    }
    this.setViewState({ screenStream: stream, isStreamLoading: true });
    this.attachVideo(stream);
  }

  onRemoteStreamRemoved(stream: IStream) {
    ErrorHandler.addBreadcrumb("Co-browse | remote stream removed");
    this.dispatchDisplayCaptureStateChange(null);
    this.dispatchDisplayCaptureDirectionChange(null);
    if (this.$conversationType === ConversationTypeEnum.chat) {
      this.dispatchConversationMinimizeChange(false);
    }
    this.resetStream(stream);
  }

  resetStream(stream: IStream) {
    if (stream.id === this.viewState.screenStream?.id) {
      this.setViewState({
        screenStream: undefined,
        isStreamMetadataLoaded: false,
      });
    }
  }

  resetState() {
    this.setViewState({ ...DEFAULT_COBROWSE_STATE });
    document.getElementById(STYLESHEET_ID)?.remove();
    CobrowseService.clearSessionStorage();
  }

  attachVideo(stream: IStream) {
    if (this.videoRef) {
      this.videoRef.srcObject = stream.mediaStream;
      this.videoRef.play();
    }
  }

  /** view accessors */

  get isConnecting() {
    return this.$cobrowseState === CobrowseStateEnum.connecting;
  }
  get isConnected() {
    return this.$cobrowseState === CobrowseStateEnum.connected;
  }
  get isControlled() {
    return (
      this.$cobrowseState === CobrowseStateEnum.connected &&
      this.$permissions.control
    );
  }
  get isTerminating() {
    return this.$cobrowseState === CobrowseStateEnum.terminating;
  }
  get isOriginLauncher() {
    return this.$cobrowseOrigin === CobrowseOriginEnum.widget;
  }
  get isPositionLeft() {
    return this.themeOptions.positionHorizontal === "left";
  }
  get isPositionRight() {
    return this.themeOptions.positionHorizontal === "right";
  }
  get isPositionCenter() {
    return this.themeOptions.positionHorizontal === "center";
  }
  get isPositionTop() {
    return this.themeOptions.positionVertical === "top";
  }
  get isPositionBottom() {
    return this.themeOptions.positionVertical === "bottom";
  }
  get isDisplayCaptureRequest() {
    return this.$cobrowseRequestType === "display-capture";
  }
  get isCobrowseRequest() {
    return this.$cobrowseRequestType === "view" || !this.$cobrowseRequestType;
  }
  get isSharingDisplay() {
    return (
      this.$displayCaptureState === DisplayCaptureStateEnum.sharing &&
      this.$displayCaptureDirection === DisplayCaptureDirectionEnum.outgoing
    );
  }
  get stateText() {
    switch (this.$cobrowseState) {
      case CobrowseStateEnum.connecting:
        return TranslateService.instant("Connecting...");
      case CobrowseStateEnum.connected:
        if (this.$displayCaptureState) {
          // if we have a display capture session, show that info
          switch (this.$displayCaptureState) {
            case DisplayCaptureStateEnum.loadingStream:
            case DisplayCaptureStateEnum.requestingAccess:
              return TranslateService.instant("Requesting access...");
            case DisplayCaptureStateEnum.sharing:
              return this.$displayCaptureDirection ===
                DisplayCaptureDirectionEnum.outgoing
                ? TranslateService.instant("Agent is viewing your window")
                : TranslateService.instant("Agent is sharing");
          }
        } else {
          if (this.$permissions.control) {
            return TranslateService.instant("Agent is controlling your page");
          } else if (this.$permissions.view) {
            return TranslateService.instant("Agent is viewing your page");
          } else {
            return TranslateService.instant("Connected");
          }
        }
      case CobrowseStateEnum.terminating:
        return TranslateService.instant("Closing...");
    }
  }

  /** view  */

  render() {
    const startText = TranslateService.instant("Start now");
    let title, description;
    // const
    switch (this.$cobrowseRequestType) {
      case "display-capture":
        title = TranslateService.instant("Start a 'Screen Share' session");
        description = TranslateService.instant("Share screen");
        break;
      case "view":
      default:
        title = TranslateService.instant("Start a co-browse session");
        description = TranslateService.instant("Co-browsing");
        break;
    }
    const trayClass = {
      "cobrowse-tray": true,
      "cobrowse-tray-left": this.isPositionLeft,
      "cobrowse-tray-right": this.isPositionRight,
      "cobrowse-tray-top": this.isPositionTop,
      "cobrowse-tray-bottom": this.isPositionBottom,
      "cobrowse-tray-mobile": this.isMobile,
      "cobrowse-tray-center": this.isPositionCenter,
    };

    const streamCardClass = {
      "stream-container": true,
      "stream-container-loading": this.viewState.isStreamLoading,
      "stream-container-ready": this.viewState.isStreamMetadataLoaded,
      "stream-container-bottom": this.isPositionBottom,
      "stream-container-top": this.isPositionTop,
      [`stream-container-${this.$displayCaptureDirection}`]: true,
    };
    const cobrowsePinLabel = TranslateService.instant(
      "Please enter the 7-digit code provided by the agent."
    );
    const sender =
      this.$cobrowseRemoteEndpointMetadata?.name ||
      TranslateService.instant("agent");
    return (
      <Host>
        {this.viewState.isViewPermissionEnabled && (
          <av-pointer
            top={this.viewState.pointerPosition.top}
            left={this.viewState.pointerPosition.left}
            sender={sender}
          ></av-pointer>
        )}

        {((this.isConnecting && !this.viewState.isOpenTicketDialog) ||
          this.isConnected ||
          this.isControlled) &&
          this.isOriginLauncher && (
            <div class={trayClass} ref={(e) => this.containerReady.emit(e)}>
              <div>
                {this.isDisplayCaptureRequest && (
                  <av-asset image="cobrowse-screen"></av-asset>
                )}
                {this.isCobrowseRequest && (
                  <av-asset image="cobrowse"></av-asset>
                )}

                <div class="cobrowse-tray-info av-direction">
                  <b class="blink">{description}</b>
                  <small>{this.stateText}</small>
                </div>
              </div>
              {(AppConfigService.cobrowseDisplayCaptureEnabled ||
                this.isDisplayCaptureRequest ||
                this.isSharingDisplay) && (
                <app-display-capture></app-display-capture>
              )}

              {/* <av-button onClick={this.terminate.bind(this)} class="pulse-danger" tooltip="Cobrowse ON" shape="round" color="danger">
              <av-asset image="cobrowse"></av-asset>
            </av-button> */}

              {/* {this.isControlled &&
              <av-switch checked={this.isControlled} onChanged={this.controlPermissionChange.bind(this)} tooltip="Control Permission">
                <av-asset image="text"></av-asset>
              </av-switch>
            } */}
            </div>
          )}

        <av-card
          data-tid="cobrowse/video"
          class={streamCardClass}
          resizable={
            this.$displayCaptureDirection ===
            DisplayCaptureDirectionEnum.incoming
          }
          resizeRatio={this.viewState.streamWidth / this.viewState.streamHeight}
          open={
            !!this.viewState.screenStream &&
            this.viewState.isStreamMetadataLoaded
          }
        >
          <div slot="card-body">
            {/* {this.viewState.isStreamLoading && (
              <av-spinner dark={!this.themeOptions.darkMode}></av-spinner>
            )} */}
            <video
              autoplay
              playsinline
              muted
              onLoadedMetaData={this.onVideoElementLoadedMetadata.bind(this)}
              ref={this.onVideoElementRef.bind(this)}
            ></video>
            {this.$displayCaptureDirection ===
              DisplayCaptureDirectionEnum.incoming && (
              <av-button
                shape="round"
                color="primary"
                size="sm"
                tooltip="Full screen"
                tooltip-position="left"
                class={{
                  "stream-full-screen": true,
                  "stream-full-screen-top":
                    this.isPositionTop &&
                    (this.isPositionCenter || this.isPositionLeft),
                }}
                onClick={this.onVideoElementFullScreen.bind(this)}
              >
                <av-asset image="expand" color="light"></av-asset>
              </av-button>
            )}
          </div>
        </av-card>

        <av-card
          role={this.viewState.isOpenTicketDialog ? "dialog" : null}
          open={this.viewState.isOpenTicketDialog}
          class="av-card-ticket"
          data-tid="cobrowse/ticket"
        >
          <div slot="card-body">
            <h3 class="title av-direction">
              <av-translate>{title}</av-translate>
            </h3>
            <p class="av-direction">
              <av-translate>{cobrowsePinLabel}</av-translate>
            </p>
            <av-pin
              ariaLabel={cobrowsePinLabel}
              value={this.viewState.ticket}
              digits={this.ticketLength}
              onChanged={(e) => this.setTicketID((e as CustomEvent).detail)}
            ></av-pin>
            {this.viewState.isTicketInvalid && (
              <span role="alert" aria-live="assertive" class="error">
                <av-translate>Invalid code</av-translate>
              </span>
            )}
          </div>
          <div class="card-footer" slot="card-footer">
            <div>
              {AppConfigService.cobrowseDisclaimerURL && (
                <av-disclaimer
                  url={AppConfigService.cobrowseDisclaimerURL}
                  onAcceptedChange={(e) =>
                    this.setViewState({ isDisclaimerAccepted: e.detail })
                  }
                ></av-disclaimer>
              )}
            </div>
            <av-button
              data-tid="cobrowse/start"
              color="accent"
              disabled={
                !!AppConfigService.cobrowseDisclaimerURL
                  ? !this.viewState.isDisclaimerAccepted
                  : false
              }
              ariaLabel={TranslateService.instant("Start co-browse session")}
              loading={this.viewState.loading}
              onClick={this.start.bind(this)}
            >
              {startText}
            </av-button>
          </div>
        </av-card>
      </Host>
    );
  }
}
